import {
  IPricingAsset,
  IPricingCategory,
  IPricingSectionV2,
  IPricingTier,
} from '@/sanity/lib/definitions';

/** Canonical quiz keys — match the Sanity pricingSectionV2 keys 1:1. */
export type QuizAsset = 'cfd_crypto' | 'futures';
export type QuizProduct = 'core' | 'rapid' | 'bolt';
export type QuizSize = '50k' | '100k' | '200k';

export const QUIZ_ASSETS: QuizAsset[] = ['cfd_crypto', 'futures'];
export const QUIZ_PRODUCTS: QuizProduct[] = ['core', 'rapid', 'bolt'];
export const QUIZ_SIZES: QuizSize[] = ['50k', '100k', '200k'];

/** Maps a quiz size to the Sanity tier key used in pricingSectionV2. */
export const SIZE_TO_TIER_KEY: Record<QuizSize, string> = {
  '50k': 'advanced',
  '100k': 'expert',
  '200k': 'master',
};

/** Account size in dollars — drives the earning-potential sublines on Q4. */
export const SIZE_AMOUNT: Record<QuizSize, number> = {
  '50k': 50_000,
  '100k': 100_000,
  '200k': 200_000,
};

export type PlanAvailability = (
  product: QuizProduct,
  asset: QuizAsset,
  size: QuizSize,
) => boolean;

/**
 * Static product × asset × size availability table.
 *
 * Mirrors the live Sanity pricingSectionV2 document (Aug 2026): $200k (master)
 * exists only for Core / CFD + Crypto. Used as the resolver default and in
 * tests. At runtime the quiz derives availability from the section data via
 * `sectionPlanAvailability`, so Sanity stays the single source of truth and
 * this table never has to be edited when a new size ships.
 */
export const DEFAULT_PLAN_AVAILABILITY: Record<
  QuizProduct,
  Record<QuizAsset, QuizSize[]>
> = {
  core: { cfd_crypto: ['50k', '100k', '200k'], futures: ['50k', '100k'] },
  rapid: { cfd_crypto: ['50k', '100k'], futures: ['50k', '100k'] },
  bolt: { cfd_crypto: ['50k', '100k'], futures: ['50k', '100k'] },
};

export const defaultPlanAvailability: PlanAvailability = (product, asset, size) =>
  DEFAULT_PLAN_AVAILABILITY[product][asset].includes(size);

export type QuizPlanMatch = {
  asset: IPricingAsset;
  category: IPricingCategory;
  tier: IPricingTier;
};

/** Finds the concrete Sanity tier for a product × asset × size combination. */
export function findQuizPlan(
  section: IPricingSectionV2 | undefined,
  product: QuizProduct,
  asset: QuizAsset,
  size: QuizSize,
): QuizPlanMatch | undefined {
  const assetDoc = section?.assets?.find((a) => a.key === asset);
  const category = assetDoc?.categories?.find((c) => c.key === product);
  const tier = category?.tiers?.find((t) => t.key === SIZE_TO_TIER_KEY[size]);
  if (!assetDoc || !category || !tier) return undefined;
  return { asset: assetDoc, category, tier };
}

/** Availability derived from the live section data. */
export function sectionPlanAvailability(
  section: IPricingSectionV2 | undefined,
): PlanAvailability {
  if (!section?.assets?.length) return defaultPlanAvailability;
  return (product, asset, size) => !!findQuizPlan(section, product, asset, size);
}
